'use client';
import { ButtonBase, Typography } from '@mui/material';
import { useRouter } from 'next/navigation';
import { CENTERED_ICON, COLORS, MOTION, RADIUS } from '../../constants';
import { MaterialIcon } from '../materialIcon';

/**
 * Back arrow icon button that triggers prop function or navigates to previous page when clicked using next navigation router.
 *
 * @param {string} [route] - Where to go; the previous page by default.
 * @param {string} [label] - Desktop only: text next to the arrow, and the button's name.
 * @return {React.JSX.Element} The rendered back arrow icon button.
 */
export const BackArrow = ({ route, label }: { route?: string; label?: string }) => {
  const router = useRouter();
  return (
    <ButtonBase
      data-testid="backarrow"
      aria-label={label || 'Volver'}
      onClick={() => (route ? router.push(route) : router.back())}
      // The default ripple filled the button with a dark square; fade on press instead, like the text buttons
      disableRipple
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: { xs: 1.25, md: 0 },
        // Phones keep a 44px touch target, pulled back so the arrow lines up with the content under it
        ml: { xs: -1.25, md: 0 },
        cursor: 'pointer',
        width: 'fit-content',
        font: 'inherit',
        textAlign: 'left',
        borderRadius: `${RADIUS.full}px`,
        transition: `opacity ${MOTION.fast}ms ${MOTION.easeOut}`,
        '&:hover': { opacity: 0.8 },
        '&:active': { opacity: 0.6 },
        '&.Mui-focusVisible': { outline: `2px solid ${COLORS.brand.main}`, outlineOffset: 2 },
      }}
    >
      {/* CENTERED_ICON: the inline lineHeight lost to MaterialIcon's `inherit !important`, so the glyph sat high */}
      <MaterialIcon icon="arrow_back" color="primary" aria-hidden="true" pointer sx={CENTERED_ICON} />
      {label && (
        <Typography
          variant="body2"
          color="disabled"
          sx={{
            display: { xs: 'none', md: 'block' },
          }}
        >
          {label}
        </Typography>
      )}
    </ButtonBase>
  );
};
